import DatasetLinked from "@mui/icons-material/DatasetLinked";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import JoinInnerOutlinedIcon from "@mui/icons-material/JoinInnerOutlined";
import ContentCut from '@mui/icons-material/ContentCut';
import LaunchIcon from "@mui/icons-material/Launch";
import TableChartIcon from '@mui/icons-material/TableChart';
import SavingsIcon from '@mui/icons-material/Savings';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AnalystInstituteIcon from './AnalystInstituteIcon';
import {
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { memo, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useRouteLoaderData,
} from "react-router-dom";

import ProjectRoleLabel from "components/ProjectRoleLabel";
import useLayoutState from "hooks/useLayoutState";
import getCurrentUser from "hooks/getCurrentUser";
import getConfig from "hooks/getConfig";
import { drawerWidth } from "themes/constants";
import { hasWriteAccess } from "utils/userPermissions";
import { USER_ROLES } from "constants/constants";

export default memo(Sidebar);

function Sidebar({ window = null }) {
  Sidebar.propTypes = {
    window: PropTypes.object,
  };

  const theme = useTheme();
  const {
    isSidebarDrawerOpen,
    toggleIsSidebarDrawerOpen,
    setIsSidebarDrawerOpen,
  } = useLayoutState();

  const loggedInUser = getCurrentUser();
  const { partners } = loggedInUser;
  const { activeProjectDetails } = useRouteLoaderData("projectRoot");
  const matchUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const { projectId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Use project-specific feature flags from loader, fall back to global FLAGS
  const projectFlags = activeProjectDetails?.featureFlags || {};
  const { FLAGS: globalFlags } = getConfig();
  const FLAGS = Object.keys(projectFlags).length > 0 ? projectFlags : globalFlags;

  const showListCutter = FLAGS?.LIST_CUTTER;
  const showCrosstabs = FLAGS?.CROSSTABS;
  const showBudget = FLAGS?.BUDGET;
  const showHartBdp = FLAGS?.HART_BDP;
  const showMatching = FLAGS?.CATALIST_MATCHING || FLAGS?.TARGETSMART_MATCHING;
  const showSyncs = FLAGS?.SYNCS;
  const showFileUploader = FLAGS?.FILE_UPLOADER;
  const showRegistry = FLAGS?.RESEARCH_REGISTRY;

  const [anchorEl, setAnchorEl] = useState(null);
  const isProjectMenuOpen = Boolean(anchorEl);

  const container = window ? () => window.document.body : undefined;

  const { project, roles } = activeProjectDetails;
  const defaultProjectNameText = "No project name";
  const { name: displayName } = project || { name: false };
  const userActiveRoles = loggedInUser.roles.filter(role => role.partner_gcp_project_id === projectId);
  // Allow Admin, Engineer, and Editor to create/edit resources (hasWriteAccess from userPermissions)
  const userHasWriteAccess = hasWriteAccess(userActiveRoles);
  // Check if user is an admin (role.level may come from API as string or number)
  const isAdmin = userActiveRoles.some(
    (role) => Number(role.level) === USER_ROLES.ADMIN.level
  );

  // ============================================================================
  // SIDEBAR NAVIGATION ITEMS - MANUAL ORDER
  // ============================================================================
  // Items will appear in the sidebar in the exact order they are added below.
  // To reorder items, simply move the if-blocks around.
  // ============================================================================
  
  let visibleNavItems = [];

  // Add Budget if feature flag is enabled
  if (showBudget) {
    visibleNavItems.push({
      to: `/projects/${projectId}/budget`,
      label: "Budget",
      icon: <SavingsIcon />,
    });
  }

  // Add crosstabs if feature flag is enabled
  if (showCrosstabs) {
    visibleNavItems.push({
      to: `/projects/${projectId}/crosstabs`,
      label: "Crosstabs",
      icon: <TableChartIcon />,
    });
  }

  // Add File Uploader if feature flag is enabled
  if (showFileUploader) {
    visibleNavItems.push({
      to: `/projects/${projectId}/file-uploader`,
      label: "File Uploader",
      icon: <CloudUploadIcon />,
      end: true,
    });
  }

  // Add Hart BDP if feature flag is enabled
  if (showHartBdp) {
    visibleNavItems.push({
      to: `/projects/${projectId}/hart`,
      label: "Hart Jobs",
      icon: <Diversity1Icon />,
      end: true, 
    });
    // Hart Admin: only for Admins when Hart BDP is enabled
    if (isAdmin) {
      visibleNavItems.push({
        to: `/projects/${projectId}/hart/admin`,
        label: "Hart Admin",
        icon: <AdminPanelSettingsIcon />,
      });
    }
  }

  // Add Research Registry (the pre-registration dashboard/wizard) for everyone with
  // the flag enabled — community members register and track their own experiments.
  if (showRegistry) {
    visibleNavItems.push({
      to: `/projects/${projectId}/registry`,
      label: "Research Registry",
      // The Analyst Institute's logo mark, in the same line style as the other nav icons.
      icon: <AnalystInstituteIcon />,
      end: true,
    });
  }

  // Add list cutter if feature flag is enabled
  if (showListCutter && userHasWriteAccess) {
    visibleNavItems.push({
      to: `/projects/${projectId}/list-cutter`,
      label: "List Cutter (Preview)",
      icon: <ContentCut />,
    });
  }

  // Add matching jobs if feature flag is enabled
  if (showMatching) {
    visibleNavItems.push({
      to: `/projects/${projectId}/matching-jobs`,
      label: "Matching Jobs",
      icon: <JoinInnerOutlinedIcon />,
    });
  }

  // Add Syncs if feature flag is enabled
  if (showSyncs) {
    visibleNavItems.push({
      to: `/projects/${projectId}/syncs/managed`,
      label: "Syncs",
      icon: <HubOutlinedIcon />,
    });
  }

  // ============================================================================
  // END SIDEBAR NAVIGATION ITEMS
  // ============================================================================

  const activeLinkStyles = {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.main,
  };

  const onClickMenuItem = () => {
    if (!matchUpMd) setIsSidebarDrawerOpen(false);
  };

  const onClickSwitchProject = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const onClickDifferentProject = (newProjectId) => {
    setAnchorEl(null);
    // Navigate to the project welcome page when switching projects
    navigate(`/projects/${newProjectId}`);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Navigation items will display in the order they were added above
  const navList = visibleNavItems?.length ? (
    <List>
      {visibleNavItems?.map((visibleRoute, idx) => (
        <ListItem key={idx}>
          <ListItemButton
            component={NavLink}
            to={visibleRoute.to}
            end={visibleRoute.end}
            style={({ isActive }) => (isActive ? activeLinkStyles : undefined)}
            onClick={onClickMenuItem}
          >
            <ListItemIcon
              sx={{
                my: "auto",
                color: "inherit",
                minWidth: !visibleRoute?.icon ? 18 : 36,
              }}
            >
              {visibleRoute.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant={"h5"} color="inherit">
                  {visibleRoute.label}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  ) : null;

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : "auto" }}
      aria-label="Primary navigation"
    >
      <Drawer
        container={container}
        variant={matchUpMd ? "persistent" : "temporary"}
        anchor="left"
        open={isSidebarDrawerOpen}
        onClose={() => toggleIsSidebarDrawerOpen()}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: "none",
            height: !matchUpMd ? "calc(100vh - 56px)" : "calc(100vh - 88px)",
            paddingLeft: "16px",
            paddingRight: "16px",
            [theme.breakpoints.up("md")]: {
              top: "85px",
            },
            [theme.breakpoints.down("md")]: {
              paddingTop: "20px",
            },
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            ml: 1,
          }}
        >
          <Typography variant="h5">ACTIVE PROJECT</Typography>
          {partners.length > 1 && (
            <>
              <Button
                id="basic-button"
                aria-controls={isProjectMenuOpen ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={isProjectMenuOpen ? "true" : undefined}
                onClick={onClickSwitchProject}
              >
                Switch Project
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isProjectMenuOpen}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                {partners.length > 1 && partners
                  .filter((project) => project.id !== projectId)
                  .sort((a, b) => (a.slug || '').localeCompare(b.slug || ''))
                  .map(({ name, gcp_project_id }) => (
                    <MenuItem
                      key={gcp_project_id}
                      onClick={() => onClickDifferentProject(gcp_project_id)}
                    >
                      <ListItemIcon>
                        <DatasetLinked />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <>
                            {name ?? defaultProjectNameText}
                          </>
                        }
                        secondary={gcp_project_id}
                      />
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        </Box>
        <Box
          sx={{
            pl: 2,
            pr: 1,
            borderColor: "grey.400",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DatasetLinked />
          <Box sx={{ ml: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: "3px" }}>
              {displayName && (
                <Typography
                  sx={{
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    color: "grey.800",
                  }}
                >
                  {displayName ?? defaultProjectNameText}
                </Typography>
              )}
              {roles && <ProjectRoleLabel projectRoles={roles} />}
            </Box>
            <Typography sx={{ mb: "5px" }}>{projectId}</Typography>
            {projectId && loggedInUser?.email && (
              <Link
                href={`https://console.cloud.google.com/bigquery/?p=${projectId}&project=${projectId}&authUser=${loggedInUser.email}`}
                target="_blank"
                sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}
              >
                View PAD in BigQuery
                <LaunchIcon sx={{ ml: 1, fontSize: "14px" }} />
              </Link>
            )}
          </Box>
        </Box>
        <Divider sx={{ mt: 2 }} />
        {navList}
      </Drawer>
    </Box>
  );
}
